import { useCreateTirage } from "@/api/tirages/tirage.api";
import {
  appendNoDuplicateRecordNumberRefine,
  tirageSchema,
} from "@/api/tirages/tirage.validation";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

const schema = appendNoDuplicateRecordNumberRefine(
  tirageSchema.pick({
    tirageDescriptionNumeroEnregistrement: true,
  }),
);

type FormType = z.infer<typeof schema>;

type NewTirageDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateTirage: (tirage: { id: string; tirageDescriptionNumeroEnregistrement?: string }) => void;
  artworkId: string;
};

const NewTirageDialog = ({
  onOpenChange,
  open,
  artworkId,
  onCreateTirage,
}: NewTirageDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      tirageDescriptionNumeroEnregistrement: "",
    },
  });
  const { mutateAsync } = useCreateTirage();

  const onFormSubmit = async (data: FormType, shouldRedirect: boolean) => {
    try {
      const createdTirage = await mutateAsync({
        artworkId,
        tirageDescriptionNumeroEnregistrement:
          data.tirageDescriptionNumeroEnregistrement,
      });
      if (!shouldRedirect) {
        toast({
          title: t("newRecordSuccessfullyCreated"),
          duration: 3000,
          variant: "success",
        });
        form.reset();
        return;
      }
      onCreateTirage({
        id: createdTirage.id,
        tirageDescriptionNumeroEnregistrement: createdTirage.tirageDescriptionNumeroEnregistrement,
      });
      onOpenChange(false);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("tirageDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createTirage")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="tirageDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("tirageDescriptionNumeroEnregistrement")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewTirageDialog;
